import { Suspense, useCallback, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { RouteFallback } from '../../router/lazyRoute';
import FeaturedProfiles from './FeaturedProfiles';
import PromoContent from '../Promotions';
import ErrorBoundary from '../ErrorBoundary';
import ChevronIcon from '../../assets/icons/ChevronIcon';
import { PROMO_CONFIG } from '../../config/promotions';
import './browseLayout.css';

const browsePageName = (pathname: string): string => {
    if (pathname.startsWith('/search')) return 'search';
    if (pathname.startsWith('/liked')) return 'liked';
    if (pathname.startsWith('/jamboard')) return 'jamboard';
    if (pathname.startsWith('/collaborations')) return 'collaborations';
    return 'discover';
};

const BrowseLayout = () => {
    const { pathname } = useLocation();
    const pageName = browsePageName(pathname);
    const [showMobileFeatured, setShowMobileFeatured] = useState(() => {
        const savedState = localStorage.getItem(PROMO_CONFIG.SESSION_KEYS.FEATURED_MEMBERS_VISIBILITY);
        return savedState === null ? true : savedState === 'true';
    });

    const toggleMobileFeatured = useCallback(() => {
        setShowMobileFeatured(prev => {
            const newValue = !prev;
            localStorage.setItem(PROMO_CONFIG.SESSION_KEYS.FEATURED_MEMBERS_VISIBILITY, String(newValue));
            return newValue;
        });
    }, []);

    return (
        <div className="flex-1">
            <div className="flex flex-col w-full bg-neutral-100">
                <div className="browse-stack flex w-full flex-col">
                    {!showMobileFeatured && (
                        <div className="md:hidden flex justify-center my-2">
                            <button
                                onClick={toggleMobileFeatured}
                                className="flex items-center justify-center px-3 py-0.5 bg-white rounded text-xs font-medium text-gray-500 hover:bg-gray-50 border border-gray-200 transition-colors mobile-touch-target featured-members-toggle"
                                aria-label="Show featured members"
                            >
                                <span>Show Featured Members</span>
                                <ChevronIcon className="h-3 w-3 ml-0.5" rotate={true} />
                            </button>
                        </div>
                    )}

                    {showMobileFeatured && (
                        <div className="md:hidden h-[280px] min-h-0 min-w-40 relative overflow-hidden featured-profiles-container">
                            <div className="absolute top-0 left-1/2 transform -translate-x-1/2 z-10">
                                <button
                                    onClick={toggleMobileFeatured}
                                    className="flex items-center justify-center px-2.5 py-0.5 bg-white rounded text-xs font-medium text-gray-500 hover:bg-gray-50 border border-gray-200 transition-colors mobile-touch-target featured-members-toggle"
                                    aria-label="Hide featured members"
                                >
                                    <span>Hide</span>
                                    <ChevronIcon className="h-3 w-3 ml-0.5" />
                                </button>
                            </div>
                            <ErrorBoundary>
                                <FeaturedProfiles key={pageName} mode="fixed" />
                            </ErrorBoundary>
                        </div>
                    )}

                    <div className="browse-columns flex min-h-0 max-lg:h-full max-lg:w-full">
                        <div className="flex h-full min-h-0 min-w-0 flex-1 flex-col">
                            {/* Sibling browse pages (liked, search, jamboard, ...) are lazy chunks. */}
                            <Suspense fallback={<RouteFallback />}>
                                <Outlet />
                            </Suspense>
                        </div>
                        <div className="page-panel hidden min-h-0 shrink-0 md:flex md:w-[380px]">
                            <div className="flex h-full min-h-0 w-full flex-col gap-4">
                                <div className="min-h-0 flex-1 overflow-hidden">
                                    <ErrorBoundary>
                                        <FeaturedProfiles key={pageName} mode="fluid" />
                                    </ErrorBoundary>
                                </div>
                                <div className="flex-shrink-0">
                                    <ErrorBoundary>
                                        <PromoContent pageName={pageName} />
                                    </ErrorBoundary>
                                </div>
                            </div>
                        </div>
                    </div>
                    {PROMO_CONFIG.POPUP_PROMO_PAGES.includes(pageName) && (
                        <div className="md:hidden">
                            <ErrorBoundary>
                                <PromoContent isPopup={true} pageName={pageName} />
                            </ErrorBoundary>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default BrowseLayout;
